<template>
  <div class="hms-page">
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-heart-pulse me-2"></i>{{ $t('page.wellness', 'Wellness & Health Talks') }}</h4>
        </div>
        <button class="hms-btn hms-btn-primary" @click="openAdd"><i class="mdi mdi-plus me-1"></i>New article</button>
      </div>

      <div class="hms-stat-row mb-3">
        <div v-for="t in topics" :key="t.topic_id" class="hms-stat" :class="{ selected: filterTopic === t.topic_id }" @click="filterTopic = filterTopic === t.topic_id ? null : t.topic_id; loadArticles()">
          <div class="hms-stat-num">{{ t.icon }}</div>
          <div class="hms-stat-lbl">{{ t.topic_name }}</div>
        </div>
      </div>

      <div v-if="!articles.length" class="hms-muted text-center py-5">No articles yet for this topic. Click <strong>New article</strong> to add one.</div>

      <div v-else class="row g-3">
        <div v-for="a in articles" :key="a.article_id" class="col-md-6 col-lg-4">
          <div class="hms-article-card" @click="openView(a)">
            <div class="hms-article-topic"><span>{{ a.icon }}</span> {{ a.topic_name }}</div>
            <div class="hms-article-title">{{ a.title }}</div>
            <div class="hms-article-summary">{{ a.summary }}</div>
            <div class="hms-article-meta"><i class="mdi mdi-eye me-1"></i>{{ a.view_count }} views · {{ formatDate(a.published_at) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add / Edit modal -->
    <div v-if="showForm" class="hms-modal-bg" @click.self="showForm=false">
      <div class="hms-modal" style="max-width:760px;">
        <div class="hms-modal-head">
          <h5>{{ form.article_id ? 'Edit article' : 'New article' }}</h5>
          <button class="hms-icon-btn" @click="showForm=false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="row g-3">
            <div class="col-md-6"><label class="hms-label">Topic *</label>
              <select v-model="form.topic_id" class="hms-input">
                <option :value="null">— Select topic —</option>
                <option v-for="t in topics" :key="t.topic_id" :value="t.topic_id">{{ t.topic_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Language</label>
              <select v-model="form.language_code" class="hms-input">
                <option value="en">English</option>
                <option value="sw">Kiswahili</option>
                <option value="maa">Maa</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Title *</label>
              <input v-model="form.title" class="hms-input" placeholder="e.g. Why immunisation matters before your child turns 1" />
            </div>
            <div class="col-12"><label class="hms-label">Summary</label>
              <input v-model="form.summary" class="hms-input" placeholder="Short blurb shown on the card" />
            </div>
            <div class="col-12"><label class="hms-label">Cover image URL</label>
              <input v-model="form.cover_image" class="hms-input" placeholder="https://…" />
            </div>
            <div class="col-12"><label class="hms-label">Body * (markdown / plain text — paragraphs separated by blank lines)</label>
              <textarea v-model="form.body" rows="10" class="hms-input" placeholder="Write your health talk here…"></textarea>
            </div>
            <div class="col-md-6"><label class="hms-label">Status</label>
              <select v-model="form.status" class="hms-input">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-neutral" @click="showForm=false">Cancel</button>
          <button class="hms-btn hms-btn-primary" @click="save" :disabled="saving || !form.title || !form.topic_id || !form.body">
            <i class="mdi mdi-content-save me-1"></i>{{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- View modal -->
    <div v-if="viewing" class="hms-modal-bg" @click.self="viewing=null">
      <div class="hms-modal" style="max-width:760px;">
        <div class="hms-modal-head">
          <h5>{{ viewing.title }}</h5>
          <button class="hms-icon-btn" @click="viewing=null"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="hms-article-topic mb-2"><span>{{ viewing.icon }}</span> {{ viewing.topic_name }}</div>
          <div v-if="viewing.cover_image" class="mb-3"><img :src="viewing.cover_image" style="max-width:100%; border-radius:8px;" /></div>
          <div style="white-space:pre-wrap;">{{ viewing.body }}</div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-amber" @click="openEdit(viewing)"><i class="mdi mdi-pencil me-1"></i>Edit</button>
          <button class="hms-btn hms-btn-danger" @click="remove(viewing)"><i class="mdi mdi-trash-can me-1"></i>Delete</button>
          <button class="hms-btn hms-btn-neutral" @click="viewing=null">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { executeJson, hms } from "@/api";

export default {
  name: "WellnessContent",
  data() {
    return {
      topics: [], articles: [], filterTopic: null,
      showForm: false, saving: false, viewing: null,
      form: { article_id: null, topic_id: null, title: '', summary: '', body: '', language_code: 'en', cover_image: '', status: 'published' }
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.loadTopics().then(() => this.loadArticles());
  },
  methods: {
    async loadTopics() {
      var r = await executeJson({ function: "listWellnessTopics" }, hms);
      if (r.data && r.data.success) this.topics = r.data.data.topics || [];
    },
    async loadArticles() {
      var params = { function: "wellnessFeed", lang: 'en' };
      if (this.filterTopic) params.topic_id = this.filterTopic;
      var r = await executeJson(params, hms);
      if (r.data && r.data.success) this.articles = r.data.data.articles || [];
    },
    openAdd() {
      this.form = { article_id: null, topic_id: this.filterTopic || (this.topics[0] && this.topics[0].topic_id) || null, title: '', summary: '', body: '', language_code: 'en', cover_image: '', status: 'published' };
      this.showForm = true;
    },
    openEdit(a) {
      this.form = Object.assign({ article_id: a.article_id, language_code: 'en' }, { topic_id: this.topics.find(t => t.topic_name === a.topic_name)?.topic_id }, a);
      this.viewing = null;
      this.showForm = true;
    },
    async openView(a) {
      var r = await executeJson({ function: "getWellnessArticle", article_id: a.article_id }, hms);
      if (r.data && r.data.success) this.viewing = r.data.data.article;
    },
    async save() {
      this.saving = true;
      try {
        var r = await executeJson(Object.assign({ function: "saveWellnessArticle" }, this.form), hms);
        if (r.data && r.data.success) { this.showForm = false; await this.loadArticles(); }
      } finally { this.saving = false; }
    },
    async remove(a) {
      if (!confirm('Delete "' + a.title + '"?')) return;
      await executeJson({ function: "deleteWellnessArticle", article_id: a.article_id }, hms);
      this.viewing = null;
      await this.loadArticles();
    },
    formatDate(d) { if (!d) return ''; return new Date(d).toLocaleDateString(); }
  }
};
</script>

<style scoped>
.hms-article-card { background:#fff; border:1px solid #e0e6ed; border-radius:10px; padding:16px; cursor:pointer; height:100%; display:flex; flex-direction:column; gap:6px; transition: transform .15s, box-shadow .15s; }
.hms-article-card:hover { transform: translateY(-2px); box-shadow:0 4px 12px rgba(0,71,171,.12); }
.hms-article-topic { font-size: 11pt; color: #0047AB; font-weight: 600; }
.hms-article-title { font-size: 14pt; font-weight: 700; color:#1a1a1a; }
.hms-article-summary { font-size: 10pt; color:#555; flex:1; }
.hms-article-meta { font-size: 9pt; color:#888; }
</style>
